import { Application, Container, Graphics, Sprite, Text, TilingSprite, type Texture } from 'pixi.js';
import { ARENA_HALF, ARENA_SIZE } from '../../../../shared/constants';
import { classByIndex } from '../../../../shared/catalog/classes';
import { SHAPES } from '../../../../shared/shapes';
import { getMap } from '../../../../shared/maps';
import { colors } from '../colors';
import { drawMonster, drawTelegraph } from './monsterArt';
import { getSettings } from '../settings';
import { ART_R, SHAPE_LOOKS, drawShape } from './art';
import { droneMark, drawTankParts, lookForCode } from './variants';
import { Particles } from './particles';
import { PaintCache } from './bake';
import { Fx, type FxTank } from './fx';
import { PARTS, type TankLook } from '../../../../shared/cosmetics';


export interface TankRender {
  id: number;
  x: number;
  y: number;
  aim: number;
  hull: number;
  r: number;
  hp: number;
  cls: number;
  self: boolean;
  bot: boolean;
  protected: boolean;
  disconnected: boolean;
  fired: number;
  level: number;
  name: string;
  autoAim: number;
  /** 0 standard, 1 compact, 2 lourd (décor seulement, hitbox inchangée). */
  decor: number;
  /** Skin équipé (couleurs seulement). */
  skin: number;
  shield: boolean;
  ally: boolean;
  /** Apparence peinte (cosmétiques refaits) et sa clé ; absente = dessin classique. */
  look?: TankLook;
  lookKey?: string;
}
export interface ShapeRender {
  id: number;
  x: number;
  y: number;
  angle: number;
  kind: number;
  hp: number;
}
export interface MonsterRender {
  id: number;
  x: number;
  y: number;
  angle: number;
  arch: number;
  zone: number;
  hp: number;
  boss: boolean;
  telegraph: number;
  r: number;
}
export interface BulletRender {
  id: number;
  x: number;
  y: number;
  r: number;
  own: boolean;
  /** Tiré par un coéquipier : couleur alliée, sans anneau « adverse », ignoré par les traînées et la lecture des pièges. */
  ally?: boolean;
  style: number;
  /** Tank tireur (style de variante des drones), -1 pour un monstre. */
  owner?: number;
  tx?: number;
  ty?: number;
}

interface TankView {
  root: Container;
  /** Caisse et tourelle cuites en images (bake.ts, ombrage compris) et leurs ombres portées. */
  hull: Sprite;
  turret: Sprite;
  hullShadow: Sprite;
  turretShadow: Sprite;
  /** Tourelles automatiques : géométrie partagée du cache. */
  auto: Graphics;
  lookKey: string;
  gen: number;
  look?: TankLook;
  /** Position et instant précédents (vitesse des traînées). */
  pt: number;
  hasAuto: boolean;
  shieldG: Graphics;
  shieldShown: boolean;
  decor: number;
  skin: number;
  /** Position suivie par les effets du tank (aura en orbite) : un seul objet, mis à jour à chaque image. */
  ft?: FxTank;
  label: Text;
  hpBar: Graphics;
  cls: number;
  accent: number;
  lastHp: number;
  kick: number;
  flash: number;
  seen: number;
  x: number;
  y: number;
}
interface ShapeView {
  g: Graphics;
  hpBar: Graphics;
  lastHp: number;
  seen: number;
  kind: number;
  x: number;
  y: number;
  flash: number;
}
interface MonsterView {
  body: Graphics;
  tele: Graphics;
  hpBar: Graphics;
  arch: number;
  r: number;
  lastHp: number;
  flash: number;
  seen: number;
  x: number;
  y: number;
  zone: number;
}
interface BulletView {
  g: Graphics;
  marker: Graphics | null;
  seen: number;
  x: number;
  y: number;
  own: boolean;
  ally: boolean;
  skin: number;
  style: number;
  sx: number;
  sy: number;
  heading: number;
}

/** Styles de projectiles (doivent correspondre à server/src/game/projectiles.ts STYLE). */
const S = { bullet: 0, monster: 1, drone: 2, trap: 3, mine: 4, shell: 5, missile: 6, beam: 7, orb: 8, frag: 9, mineArmed: 10 };
const BIG_BOOM = new Set([S.shell, S.mine, S.mineArmed, S.missile, S.orb]);

const BG = 0x151a20;
const OUTSIDE = 0x0c0f13;

export class Renderer {
  readonly app: Application;
  readonly world = new Container();
  private ground = new Container();
  private zoneLayer = new Container();
  /** Décor de la carte (zones et chiffres), seul vidé au changement de carte ou de couleurs. */
  private zoneArt = new Container();
  /** Repères au sol (impact des obus, mines repérées) : à part, pour survivre à un changement de carte. */
  private markerLayer = new Container();
  private monsterLayer = new Container();
  private monsters = new Map<number, MonsterView>();
  private mapId = '';
  private shapeLayer = new Container();
  private bulletLayer = new Container();
  private tankLayer = new Container();
  private fxLayer = new Container();
  private labelLayer = new Container();
  private tanks = new Map<number, TankView>();
  private shapes = new Map<number, ShapeView>();
  private bullets = new Map<number, BulletView>();
  private shapeTemplates: Graphics[] = [];
  private bulletTemplates = new Map<string, Graphics>();
  readonly particles: Particles;
  /** Effets cosmétiques (auras, traînées, tirs, éliminations, morts) et tanks peints cuits en images. */
  readonly fx: Fx;
  private readonly paint: PaintCache;
  private frame = 0;
  camX = 0;
  camY = 0;
  scale = 1;
  viewW = 1;
  viewH = 1;
  private shake = 0;
  private intelG = new Graphics();
  trails = false;
  mineSight = false;
  onShapeBreak?: (kind: number) => void;
  onTankHit?: (self: boolean) => void;

  private constructor(app: Application) {
    this.app = app;
    app.stage.addChild(this.world);
    this.fx = new Fx(app.renderer);
    this.paint = new PaintCache(app.renderer);
    // Traînées sous les tanks, auras et effets au-dessus ; les effets du jeu (particles.ts) restent au-dessus de tout.
    this.world.addChild(this.ground, this.zoneLayer, this.markerLayer, this.shapeLayer, this.monsterLayer, this.fx.below, this.bulletLayer, this.tankLayer, this.fx.root, this.fxLayer, this.labelLayer);
    this.zoneLayer.addChild(this.zoneArt);
    this.particles = new Particles(this.fxLayer);
    this.labelLayer.addChild(this.intelG);
    this.buildGround();
    for (const def of SHAPES) {
      const g = new Graphics();
      drawShape(g, def);
      this.shapeTemplates.push(g);
    }
  }

  /** Gabarit partagé par style × camp (moi, allié, adversaire), dessiné pour un rayon de 10 puis mis à l'échelle. */
  private bulletTemplate(style: number, own: boolean, skin = 0, ally = false): Graphics {
    const variant = style === S.drone ? lookForCode(skin).style : null;
    const side = own ? 1 : ally ? 2 : 0;
    const key = `${style}:${side}:${variant ?? ''}`;
    let g = this.bulletTemplates.get(key);
    if (g) return g;
    const pal = colors();
    const c = own ? pal.self : ally ? pal.ally : style === S.monster ? pal.monster : pal.enemy;
    const ink = own || ally ? 0x1a3b48 : 0x4a1a10;
    g = new Graphics();
    switch (style) {
      case S.drone:
        g.poly([13, 0, -8, 9, -4, 0, -8, -9]).fill(c).stroke({ width: 2.5, color: ink });
        if (variant) droneMark(g, variant);
        break;
      case S.trap:
        for (let i = 0; i < 3; i++) {
          const a = (i * Math.PI * 2) / 3;
          g.poly([Math.cos(a) * 13, Math.sin(a) * 13, Math.cos(a + 1.05) * 4, Math.sin(a + 1.05) * 4, Math.cos(a - 1.05) * 4, Math.sin(a - 1.05) * 4]).fill(c).stroke({ width: 2, color: ink });
        }
        break;
      case S.mine:
        g.circle(0, 0, 10).fill(0x3a3f46).stroke({ width: 2.5, color: c }).circle(0, 0, 3).fill(0x6a717b);
        break;
      case S.mineArmed:
        g.circle(0, 0, 10).fill(0x3a3f46).stroke({ width: 2.5, color: c }).circle(0, 0, 3.5).fill(0xff3b2f);
        break;
      case S.shell:
        g.circle(3, 4, 10).fill({ color: 0x000000, alpha: 0.25 }).circle(0, 0, 10).fill(c).stroke({ width: 2.5, color: ink }).circle(-3, -3, 3).fill({ color: 0xffffff, alpha: 0.5 });
        break;
      case S.missile:
        g.poly([-14, -6, 6, -6, 14, 0, 6, 6, -14, 6]).fill(c).stroke({ width: 2, color: ink }).poly([-14, -4, -22, 0, -14, 4]).fill(0xffb04a);
        break;
      case S.beam:
        g.roundRect(-40, -4, 50, 8, 4).fill({ color: c, alpha: 0.85 }).roundRect(-36, -2, 44, 4, 2).fill({ color: 0xffffff, alpha: 0.9 });
        break;
      case S.orb:
        g.circle(0, 0, 15).fill({ color: c, alpha: 0.25 }).circle(0, 0, 10).fill(c).stroke({ width: 2, color: 0xffffff, alpha: 0.6 });
        break;
      case S.frag:
        g.rect(-6, -6, 12, 12).fill(c).stroke({ width: 2, color: ink });
        break;
      default:
        g.circle(0, 0, 10).fill(c).stroke({ width: 2.5, color: ink });
        // Anneau des tirs adverses (repère sans la couleur) ; jamais sur les tirs alliés, qui ne blessent pas.
        if (!own && !ally) g.circle(0, 0, 13).stroke({ width: 1.5, color: 0xffd0c0, alpha: 0.55 });
    }
    this.bulletTemplates.set(key, g);
    return g;
  }

  /**
   * Aides des modifications de détection : cercle de portée, radar des menaces visibles, boussoles
   * (boss, forme rare, agresseur récent), rayons d'aggro des monstres et alerte arrière.
   */
  drawIntel(
    d: {
      x: number;
      y: number;
      aim: number;
      info: string[];
      intel?: { boss?: [number, number]; sonar?: [number, number]; aggressor?: [number, number] };
      allies?: number[];
      enemies: [number, number][];
      mobs: [number, number, number, boolean][];
      range: number;
      rear: boolean;
    } | null,
  ): void {
    const g = this.intelG;
    g.clear();
    if (!d) return;
    const has = (k: string) => d.info.includes(k);
    if (has('rangeRing')) g.circle(d.x, d.y, d.range).stroke({ width: 2, color: 0x9fdcff, alpha: 0.35 });
    const arrow = (tx: number, ty: number, color: number, ring: number) => {
      const a = Math.atan2(ty - d.y, tx - d.x);
      const px = d.x + Math.cos(a) * ring;
      const py = d.y + Math.sin(a) * ring;
      g.poly([px + Math.cos(a) * 14, py + Math.sin(a) * 14, px + Math.cos(a + 2.5) * 9, py + Math.sin(a + 2.5) * 9, px + Math.cos(a - 2.5) * 9, py + Math.sin(a - 2.5) * 9]).fill({ color, alpha: 0.85 });
    };
    if (has('radar')) for (const [ex, ey] of d.enemies) if (Math.hypot(ex - d.x, ey - d.y) < 750) arrow(ex, ey, 0xff5a3c, 70);
    if (has('bossCompass') && d.intel?.boss) arrow(d.intel.boss[0], d.intel.boss[1], 0xb48cff, 92);
    // Balise alliée : flèches vers les coéquipiers hors de l'écran (jamais vers un adversaire).
    if (has('allyBeacon') && d.allies)
      for (let i = 0; i < d.allies.length; i += 2) if (Math.hypot(d.allies[i] - d.x, d.allies[i + 1] - d.y) > 650) arrow(d.allies[i], d.allies[i + 1], colors().ally, 76);
    if (has('sonar') && d.intel?.sonar) arrow(d.intel.sonar[0], d.intel.sonar[1], 0xffc94d, 82);
    if (has('aggressor') && d.intel?.aggressor) arrow(d.intel.aggressor[0], d.intel.aggressor[1], 0xff2a2a, 100);
    if (has('aggro'))
      for (const [mx, my, zone, aggro] of d.mobs) g.circle(mx, my, 420 + zone * 40).stroke({ width: aggro ? 3 : 1.5, color: aggro ? 0xff5a4a : 0xf0a04a, alpha: aggro ? 0.5 : 0.18 });
    if (has('rearAlert') && d.rear) {
      const a = d.aim + Math.PI;
      g.arc(d.x, d.y, 60, a - 0.6, a + 0.6).stroke({ width: 5, color: 0xff3b2f, alpha: 0.8 });
    }
  }

  /** Explosion annoncée par le serveur : onde et éclats. */
  blast(x: number, y: number, r: number): void {
    if (!this.inView(x, y, -r)) return;
    this.particles.flash(x, y, 0xffc070, r * 0.55, 0.22);
    this.particles.burst(x, y, 0xff8a3c, 10 + Math.round(r / 12), r * 2.2, 5, 0, 0.45);
  }

  static async create(parent: HTMLElement): Promise<Renderer> {
    const app = new Application();
    const q = getSettings().quality;
    await app.init({
      resizeTo: window,
      background: OUTSIDE,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2) * q,
      powerPreference: 'high-performance',
    });
    app.canvas.classList.add('game-canvas');
    parent.appendChild(app.canvas);
    app.ticker.stop(); // rendu piloté par la boucle du jeu
    return new Renderer(app);
  }

  private buildGround(): void {
    const cell = 48;
    const g = new Graphics();
    g.rect(0, 0, cell * 4, cell * 4).fill(BG);
    for (let i = 0; i <= 4; i++) {
      const major = i === 0;
      g.moveTo(i * cell, 0).lineTo(i * cell, cell * 4).stroke({ width: major ? 2 : 1, color: 0x232a33, alpha: 1 });
      g.moveTo(0, i * cell).lineTo(cell * 4, i * cell).stroke({ width: major ? 2 : 1, color: 0x232a33, alpha: 1 });
    }
    g.circle(0, 0, 2.5).fill(0x2e3743);
    const tex = this.app.renderer.generateTexture(g) as Texture;
    const tiles = new TilingSprite({ texture: tex, width: ARENA_SIZE, height: ARENA_SIZE });
    tiles.position.set(-ARENA_HALF, -ARENA_HALF);
    this.ground.addChild(tiles);
    const border = new Graphics().rect(-ARENA_HALF, -ARENA_HALF, ARENA_SIZE, ARENA_SIZE).stroke({ width: 10, color: 0x39424e });
    this.ground.addChild(border);
  }

  private playArea = new Graphics();
  private pointsG = new Graphics();

  /** Bordure de l'aire jouable (plus petite dans les modes équipes et survie). */
  setPlayArea(half: number): void {
    this.playArea.clear();
    if (!this.playArea.parent) this.zoneLayer.addChild(this.playArea);
    this.playArea.rect(-half, -half, half * 2, half * 2).stroke({ width: 12, color: 0xd6c2ff, alpha: 0.6 });
  }

  /** Points de domination : cercle, couleur du propriétaire, progression de capture. */
  setPoints(points: { x: number; y: number; owner: number; progress: number; team: number }[] | null): void {
    const g = this.pointsG;
    if (!g.parent) this.zoneLayer.addChild(g);
    g.clear();
    if (!points) return;
    const colors = [0x39d0ff, 0xff7a5a];
    points.forEach((p, i) => {
      const c = p.owner >= 0 ? colors[p.owner] : 0x8d99a6;
      g.circle(p.x, p.y, 230).fill({ color: c, alpha: 0.08 }).stroke({ width: 6, color: c, alpha: 0.7 });
      if (p.progress > 0 && p.team >= 0) g.arc(p.x, p.y, 245, -Math.PI / 2, -Math.PI / 2 + p.progress * Math.PI * 2).stroke({ width: 10, color: colors[p.team], alpha: 0.9 });
      void i;
    });
  }

  /** Mode de couleurs changé : projectiles, tanks, monstres et zones sont redessinés avec la nouvelle palette. */
  applyColors(): void {
    // Les gabarits sont partagés par tous les projectiles vivants : chacun est d'abord rebranché sur un gabarit neuf,
    // puis seulement les anciens sont libérés. Les détruire avant rendait les projectiles invisibles (contexte vide)
    // et faisait échouer le rendu.
    const old = [...this.bulletTemplates.values()];
    this.bulletTemplates.clear();
    for (const v of this.bullets.values()) v.g.context = this.bulletTemplate(v.style, v.own, v.skin, v.ally).context;
    for (const g of old) g.destroy();
    for (const v of this.tanks.values()) v.accent = -1;
    for (const v of this.monsters.values()) {
      drawMonster(v.body, v.arch, v.zone, v.r);
      v.lastHp = -1;
    }
    const map = this.mapId;
    this.mapId = '';
    if (map) this.setMap(map);
  }

  /** Dessine les zones de danger de la carte (couleur + chiffre romain : lisible sans la couleur). */
  setMap(id: string): void {
    if (id === this.mapId) return;
    this.mapId = id;
    // Seul le décor de la carte est remplacé : la bordure de l'aire jouable, les points de domination et les repères
    // au sol restent en place (ils disparaissaient jusqu'à la partie suivante).
    for (const c of this.zoneArt.removeChildren()) c.destroy();
    const map = getMap(id);
    const sorted = [...map.regions].sort((a, b) => a.level - b.level);
    const g = new Graphics();
    for (const r of sorted) {
      g.poly(r.poly).fill({ color: colors().zoneFill[r.level], alpha: 0.16 + r.level * 0.025 });
      g.poly(r.poly).stroke({ width: 6, color: colors().zoneRim[r.level], alpha: 0.55 });
    }
    this.zoneArt.addChild(g);
    const labels = ['', 'I', 'II', 'III', 'BOSS'];
    for (const r of map.regions) {
      const [x0, y0, x1, y1] = r.box;
      const t = new Text({
        text: labels[r.level],
        style: { fontFamily: 'system-ui, sans-serif', fontSize: 140, fontWeight: '900', fill: colors().zoneRim[r.level] },
      });
      t.alpha = 0.12;
      t.anchor.set(0.5);
      t.position.set((x0 + x1) / 2, r.id.endsWith('W') || r.id.endsWith('E') ? (y0 + y1) / 2 - (y1 - y0) * 0.38 * (r.level / 3) : (y0 + y1) / 2);
      this.zoneArt.addChild(t);
    }
  }

  monster(e: MonsterRender): void {
    let v = this.monsters.get(e.id);
    if (!v) {
      const body = new Graphics();
      const tele = new Graphics();
      const hpBar = new Graphics();
      drawMonster(body, e.arch, e.zone, e.r);
      this.monsterLayer.addChild(tele, body);
      this.labelLayer.addChild(hpBar);
      v = { body, tele, hpBar, arch: e.arch, r: e.r, lastHp: -1, flash: 0, seen: 0, x: e.x, y: e.y, zone: e.zone };
      this.monsters.set(e.id, v);
    }
    v.seen = this.frame;
    v.x = e.x;
    v.y = e.y;
    v.body.position.set(e.x, e.y);
    v.body.rotation = e.angle;
    if (e.hp < v.lastHp) v.flash = 0.08;
    v.body.tint = v.flash > 0 ? 0xffd6cc : 0xffffff;
    v.tele.position.set(e.x, e.y);
    if (e.telegraph) {
      const pulse = getSettings().reducedMotion ? 0.5 : 0.5 + 0.5 * Math.sin(performance.now() / 60);
      drawTelegraph(v.tele, e.telegraph, e.r, e.angle, pulse);
      v.tele.visible = true;
    } else v.tele.visible = false;
    if (e.hp !== v.lastHp && !e.boss) {
      v.hpBar.clear();
      if (e.hp < 255) {
        const w = e.r * 2;
        v.hpBar.roundRect(-w / 2, 0, w, 5, 2.5).fill(0x0b0e12);
        v.hpBar.roundRect(-w / 2 + 1, 1, Math.max(0, (w - 2) * (e.hp / 255)), 3, 1.5).fill(colors().zoneRim[e.zone]);
      }
    }
    v.hpBar.visible = !e.boss && e.hp < 255;
    v.hpBar.position.set(e.x, e.y + e.r + 8);
    v.lastHp = e.hp;
  }

  beginFrame(): void {
    this.frame++;
  }

  setCamera(x: number, y: number, viewW: number, viewH: number): void {
    const sw = this.app.screen.width;
    const sh = this.app.screen.height;
    // Remplit l'écran sans révéler plus que la vue équitable (on rogne plutôt qu'élargir).
    this.scale = Math.max(sw / viewW, sh / viewH);
    this.viewW = viewW;
    this.viewH = viewH;
    let sx = 0;
    let sy = 0;
    if (this.shake > 0 && getSettings().shake && !getSettings().reducedMotion) {
      sx = (Math.random() - 0.5) * this.shake;
      sy = (Math.random() - 0.5) * this.shake;
    }
    this.camX = x;
    this.camY = y;
    this.world.scale.set(this.scale);
    this.world.position.set(sw / 2 - (x + sx) * this.scale, sh / 2 - (y + sy) * this.scale);
  }

  worldToScreen(x: number, y: number): [number, number] {
    return [(x - this.camX) * this.scale + this.app.screen.width / 2, (y - this.camY) * this.scale + this.app.screen.height / 2];
  }

  private inView(x: number, y: number, margin: number): boolean {
    const hw = this.app.screen.width / 2 / this.scale - margin;
    const hh = this.app.screen.height / 2 / this.scale - margin;
    return Math.abs(x - this.camX) < hw && Math.abs(y - this.camY) < hh;
  }

  tank(e: TankRender): void {
    let v = this.tanks.get(e.id);
    const pal = colors();
    const accent = e.self ? pal.self : e.ally ? pal.ally : e.bot ? pal.bot : pal.enemy;
    if (!v) {
      const root = new Container();
      const hull = new Sprite();
      const turret = new Sprite();
      // Ombres portées : toujours décalées vers le bas à droite de l'écran (lumière fixe), sous le tank.
      const hullShadow = new Sprite();
      const turretShadow = new Sprite();
      hullShadow.alpha = turretShadow.alpha = 0.42;
      const auto = new Graphics();
      const shieldG = new Graphics();
      root.addChild(hullShadow, turretShadow, hull, turret, auto, shieldG);
      const label = new Text({
        text: '',
        style: { fontFamily: 'Rajdhani, system-ui, sans-serif', fontSize: 15, fontWeight: '700', fill: 0xeef3f8, stroke: { color: 0x0b0e12, width: 4 } },
      });
      label.anchor.set(0.5, 1);
      const hpBar = new Graphics();
      this.tankLayer.addChild(root);
      this.labelLayer.addChild(label, hpBar);
      v = { root, hull, turret, hullShadow, turretShadow, auto, lookKey: '', gen: -1, pt: performance.now(), hasAuto: false, shieldG, shieldShown: false, decor: 0, skin: 0, label, hpBar, cls: -1, accent: -1, lastHp: -1, kick: 0, flash: 0, seen: 0, x: e.x, y: e.y };
      this.tanks.set(e.id, v);
    }
    v.seen = this.frame;
    const now = performance.now();
    const dtv = Math.min(0.1, Math.max(0, (now - v.pt) / 1000));
    const vx = dtv > 0 ? (e.x - v.x) / dtv : 0;
    const vy = dtv > 0 ? (e.y - v.y) / dtv : 0;
    v.pt = now;
    v.x = e.x;
    v.y = e.y;
    const lk = e.look ? (e.lookKey ?? '') : '';
    if (v.decor !== e.decor || v.skin !== e.skin || v.lookKey !== lk || v.gen !== this.paint.generation) {
      v.decor = e.decor;
      v.skin = e.skin;
      v.lookKey = lk;
      v.cls = -1;
    }
    v.look = e.look;
    if (v.cls !== e.cls || v.accent !== accent) {
      const cls = classByIndex(e.cls);
      // Caisse et tourelle cuites une fois par apparence (peinte ou classique), partagées entre les tanks.
      const baked = e.look ? this.paint.get(cls, e.look, accent, e.decor) : this.paint.getClassic(cls, e.skin, accent, e.decor);
      const put = (s: Sprite, p: typeof baked.hull) => {
        s.texture = p.texture;
        s.anchor.set(p.ax, p.ay);
      };
      put(v.hull, baked.hull);
      put(v.turret, baked.turret);
      put(v.hullShadow, baked.hullShadow);
      put(v.turretShadow, baked.turretShadow);
      v.auto.context = baked.auto;
      v.hasAuto = baked.hasAuto;
      v.gen = this.paint.generation;
      v.cls = e.cls;
      v.accent = accent;
    }
    if (e.look) {
      const ft = (v.ft ??= { x: e.x, y: e.y, vx, vy, r: e.r, hull: e.hull });
      ft.x = e.x;
      ft.y = e.y;
      ft.vx = vx;
      ft.vy = vy;
      ft.r = e.r;
      ft.hull = e.hull;
      const key = String(e.id);
      this.fx.aura(key, e.look.aura, ft, dtv);
      this.fx.trail(key, e.look.trail, ft, dtv);
      if (e.fired && e.look.shot) {
        // Effet de tir à la bouche de chaque tube qui vient de tirer (la balle ne change pas).
        const s = e.r / ART_R;
        const barrels = classByIndex(e.cls).weapon.barrels;
        for (let i = 0; i < barrels.length; i++) {
          if (!((e.fired >> i) & 1) || barrels[i].auto) continue;
          const b = barrels[i];
          const a = e.aim + (b.angle * Math.PI) / 180;
          const len = b.length * ART_R * s;
          const off = b.offset * ART_R * s;
          this.fx.shot(e.look.shot, e.x + Math.cos(a) * len - Math.sin(a) * off, e.y + Math.sin(a) * len + Math.cos(a) * off, a, e.r * 0.45);
        }
      }
    }
    if (e.hp < v.lastHp - 1) {
      v.flash = 0.12;
      this.onTankHit?.(e.self);
      if (e.self) this.shake = 6;
    }
    if (e.fired) v.kick = 1;
    const s = e.r / ART_R;
    v.root.position.set(e.x, e.y);
    v.root.scale.set(s);
    v.hull.rotation = v.hullShadow.rotation = e.hull;
    v.turret.rotation = v.turretShadow.rotation = e.aim;
    v.hullShadow.position.set(2.2, 3.2);
    v.auto.visible = v.hasAuto;
    if (e.shield !== v.shieldShown) {
      v.shieldG.clear();
      if (e.shield) v.shieldG.circle(0, 0, ART_R * 1.25).stroke({ width: 3, color: 0x9cc8ff, alpha: 0.7 });
      v.shieldShown = e.shield;
    }
    v.auto.rotation = e.autoAim;
    const kick = v.kick * 3;
    v.turret.position.set(-Math.cos(e.aim) * kick, -Math.sin(e.aim) * kick);
    v.turretShadow.position.set(2.2 - Math.cos(e.aim) * kick, 3.2 - Math.sin(e.aim) * kick);
    v.root.alpha = e.protected ? 0.55 + 0.25 * Math.sin(performance.now() / 120) : e.disconnected ? 0.5 : 1;
    v.hull.tint = v.flash > 0 ? 0xffb0a0 : 0xffffff;
    v.turret.tint = v.hull.tint;
    v.auto.tint = v.hull.tint;

    const prefix = e.self ? '' : e.ally ? (e.bot ? '◆ [BOT] ' : '◆ ') : e.bot ? '[BOT] ' : '▼ ';
    const text = `${prefix}${e.name}`;
    if (v.label.text !== text) v.label.text = text;
    const inv = 1 / this.scale;
    v.label.scale.set(inv);
    v.label.position.set(e.x, e.y - e.r - 8 * inv);
    v.label.visible = !e.self;
    if (e.hp !== v.lastHp || v.hpBar.visible !== e.hp < 255) {
      v.hpBar.clear();
      if (e.hp < 255) {
        const w = e.r * 2;
        v.hpBar.roundRect(-w / 2, 0, w, 5, 2.5).fill(0x0b0e12);
        v.hpBar.roundRect(-w / 2 + 1, 1, Math.max(0, (w - 2) * (e.hp / 255)), 3, 1.5).fill(e.self ? pal.hpSelf : pal.hpOther);
      }
      v.hpBar.visible = e.hp < 255;
    }
    v.hpBar.position.set(e.x, e.y + e.r + 6);
    v.lastHp = e.hp;
  }

  shape(e: ShapeRender): void {
    let v = this.shapes.get(e.id);
    if (!v) {
      const g = new Graphics(this.shapeTemplates[e.kind].context);
      const hpBar = new Graphics();
      this.shapeLayer.addChild(g);
      this.labelLayer.addChild(hpBar);
      v = { g, hpBar, lastHp: -1, seen: 0, kind: e.kind, x: e.x, y: e.y, flash: 0 };
      this.shapes.set(e.id, v);
    }
    v.seen = this.frame;
    v.x = e.x;
    v.y = e.y;
    v.g.position.set(e.x, e.y);
    v.g.rotation = e.angle;
    if (e.hp < v.lastHp) v.flash = 0.08;
    v.g.tint = v.flash > 0 ? 0xffe2d6 : 0xffffff;
    if (e.hp !== v.lastHp) {
      v.hpBar.clear();
      if (e.hp < 255) {
        const r = SHAPES[e.kind].radius;
        v.hpBar.roundRect(-r, 0, r * 2, 4, 2).fill(0x0b0e12);
        v.hpBar.roundRect(-r + 1, 1, Math.max(0, (r * 2 - 2) * (e.hp / 255)), 2, 1).fill(0xffd27a);
      }
    }
    v.hpBar.position.set(e.x, e.y + SHAPES[e.kind].radius + 6);
    v.hpBar.visible = e.hp < 255;
    v.lastHp = e.hp;
  }

  bullet(e: BulletRender): void {
    let v = this.bullets.get(e.id);
    const ally = !!e.ally && !e.own;
    if (!v || v.style !== e.style || v.ally !== ally) {
      if (v) {
        v.g.destroy();
        v.marker?.destroy();
      }
      const skin = e.style === S.drone && e.owner !== undefined ? (this.tanks.get(e.owner)?.skin ?? 0) : 0;
      const g = new Graphics(this.bulletTemplate(e.style, e.own, skin, ally).context);
      this.bulletLayer.addChild(g);
      let marker: Graphics | null = null;
      if (e.style === S.shell && e.tx !== undefined && e.ty !== undefined) {
        // Impact télégraphié : cercle au point d'arrivée de l'obus, à la couleur du camp du tireur.
        const pal = colors();
        const c = e.own ? pal.self : ally ? pal.ally : pal.enemy;
        marker = new Graphics().circle(0, 0, 60).stroke({ width: 4, color: c, alpha: 0.6 }).circle(0, 0, 8).fill({ color: c, alpha: 0.6 });
        marker.position.set(e.tx, e.ty);
        this.markerLayer.addChild(marker);
      }
      const prev = v;
      v = { g, marker, seen: 0, x: e.x, y: e.y, own: e.own, ally, skin, style: e.style, sx: prev?.sx ?? e.x, sy: prev?.sy ?? e.y, heading: prev?.heading ?? 0 };
      this.bullets.set(e.id, v);
      if (e.own && !prev && (e.style === S.bullet || e.style === S.beam)) this.particles.flash(e.x, e.y, 0xfff3c0, e.r * 0.8, 0.08);
    }
    const dx = e.x - v.x;
    const dy = e.y - v.y;
    if (dx * dx + dy * dy > 0.5) v.heading = Math.atan2(dy, dx);
    v.seen = this.frame;
    v.x = e.x;
    v.y = e.y;
    v.g.position.set(e.x, e.y);
    let scale = e.r / 10;
    if (e.style === S.shell && e.tx !== undefined && e.ty !== undefined) {
      // Hauteur simulée : l'obus grossit à mi-parcours.
      const total = Math.hypot(e.tx - v.sx, e.ty - v.sy) || 1;
      const done = Math.min(1, Math.hypot(e.x - v.sx, e.y - v.sy) / total);
      scale *= 1 + Math.sin(done * Math.PI) * 0.8;
      if (v.marker) v.marker.alpha = 0.4 + 0.6 * done;
    }
    v.g.scale.set(scale);
    v.g.rotation = e.style === S.trap ? v.g.rotation + 0.03 : v.heading;
    // Traînées des tirs ennemis et lecture des pièges : jamais pour ses propres tirs ni ceux des coéquipiers.
    if (this.trails && !e.own && !ally && this.frame % 2 === 0) this.particles.flash(e.x, e.y, 0xff8a6a, Math.max(2, e.r * 0.45), 0.35);
    if (this.mineSight && !e.own && !ally && (e.style === S.mine || e.style === S.mineArmed) && !v.marker) {
      // Lecture des pièges : les mines adverses visibles sont cerclées.
      v.marker = new Graphics().circle(0, 0, 40).stroke({ width: 3, color: 0xffd24a, alpha: 0.8 });
      this.markerLayer.addChild(v.marker);
    }
    if (v.marker && (e.style === S.mine || e.style === S.mineArmed)) v.marker.position.set(e.x, e.y);
  }

  endFrame(dt: number): void {
    const f = this.frame;
    for (const [id, v] of this.tanks) {
      v.kick = Math.max(0, v.kick - dt * 12);
      v.flash = Math.max(0, v.flash - dt);
      if (v.seen === f) continue;
      if (this.inView(v.x, v.y, 60)) {
        this.particles.burst(v.x, v.y, 0x8b97a5, 22, 260, 7, 4, 0.8);
        this.particles.burst(v.x, v.y, 0xffa24a, 14, 200, 5, 0, 0.5);
      }
      // Les textures cuites et la géométrie partagée des tourelles appartiennent au cache : destroy ne les libère pas.
      v.root.destroy({ children: true });
      v.label.destroy();
      v.hpBar.destroy();
      this.fx.forget(String(id));
      this.tanks.delete(id);
    }
    for (const [id, v] of this.shapes) {
      v.flash = Math.max(0, v.flash - dt);
      if (v.seen === f) continue;
      if (this.inView(v.x, v.y, 40)) {
        const look = SHAPE_LOOKS[v.kind];
        this.particles.burst(v.x, v.y, look.fill, 6 + v.kind * 4, 160 + v.kind * 30, 4 + v.kind, SHAPES[v.kind].sides, 0.55);
        this.onShapeBreak?.(v.kind);
      }
      v.g.destroy();
      v.hpBar.destroy();
      this.shapes.delete(id);
    }
    for (const [id, v] of this.monsters) {
      v.flash = Math.max(0, v.flash - dt);
      if (v.seen === f) continue;
      if (this.inView(v.x, v.y, 40)) {
        this.particles.burst(v.x, v.y, colors().zoneRim[v.zone], 18, 240, 6, 3, 0.7);
        this.onShapeBreak?.(3);
      }
      v.body.destroy();
      v.tele.destroy();
      v.hpBar.destroy();
      this.monsters.delete(id);
    }
    for (const [id, v] of this.bullets) {
      if (v.seen === f) continue;
      if (this.inView(v.x, v.y, 20) && !BIG_BOOM.has(v.style)) this.particles.burst(v.x, v.y, v.own ? colors().self : v.ally ? colors().ally : colors().enemy, 3, 90, 2.5, 0, 0.25);
      v.g.destroy();
      v.marker?.destroy();
      this.bullets.delete(id);
    }
    const p = getSettings().particles;
    this.particles.budget = getSettings().reducedMotion ? 0.3 : p === 'low' ? 0.35 : p === 'medium' ? 0.7 : 1;
    this.particles.update(dt);
    this.fx.budget = getSettings().reducedMotion ? 0 : this.particles.budget;
    this.fx.update(dt);
    this.shake = Math.max(0, this.shake - dt * 30);
    this.app.render();
    this.paint.flush();
  }

  /**
   * Élimination annoncée par le serveur : effet de mort aux couleurs de la victime, effet d'élimination du tueur.
   * Les apparences sont celles connues du client (absentes = dessin classique, aucun effet cosmétique).
   */
  cosmeticKill(victimId: number, x: number, y: number, victim: TankLook | undefined, killer: TankLook | undefined): void {
    if (!this.inView(x, y, 120)) return;
    const v = this.tanks.get(victimId);
    const r = v ? v.root.scale.x * ART_R : 30;
    if (victim?.death) this.fx.death(victim.death, x, y, r, PARTS.map((p) => victim.parts[p].color));
    if (killer?.kill) this.fx.kill(killer.kill, x, y, r);
  }

  destroy(): void {
    this.paint.clear();
    // Sans libérer les réserves globales de Pixi : d'autres applications (aperçus des menus) les utilisent encore.
    this.app.destroy({ removeView: true }, { children: true });
  }
}
